const CONCIERGE_CODES = new Set([
  // Disney Adventure ship_cabintypes confirmed on 2026-09-09. Names remain
  // the primary signal; these codes cover missing/localized upstream names.
  '01A', '01B', '01C', '02A', '02B', '02C', '03A', '03B', '03C',
  '04A', '04B', '04C', '04D',
]);

export function isDisneyAdventure(cruise) {
  return Number(cruise?.ship_id ?? cruise?.shipId) === 6868
    && Number(cruise?.line_id ?? cruise?.lineId) === 73;
}

function isConcierge(room) {
  return /concierge|컨시어지/i.test(`${room?.grade_name || ''} ${room?.grade_name_ko || ''}`)
    || CONCIERGE_CODES.has(String(room?.cabincode || '').trim().toUpperCase());
}

function categoryType(category) {
  return String(category?.codtype || category?.name || '').trim().toLowerCase();
}

function isSuite(category) {
  return ['suite', '스위트'].includes(categoryType(category));
}

function availableMinPrice(rooms) {
  const prices = rooms.flatMap(room => (room.gridpricing || [])
    .filter(option => option.available === 'Y')
    .map(option => {
      if (option.price == null || String(option.price).trim() === '') return NaN;
      return Number(String(option.price).replace(/,/g, ''));
    })
    .filter(price => Number.isFinite(price) && price > 0));
  return prices.length ? Math.min(...prices) : null;
}

/** Booking step 1 presentation only. Never mutate upstream room/rate objects
 * or DB cabintype: original identifiers and enriched deck/translation data
 * must continue unchanged into deck selection and booking.
 * Safe to apply to both fresh responses and already regrouped session caches.
 */
export function regroupDisneyAdventureCabins(groups = [], cruise) {
  if (!isDisneyAdventure(cruise)) return groups;

  const moved = [];
  let suite = null;
  const result = [];
  groups.forEach(category => {
    if (isSuite(category)) {
      if (!suite) {
        suite = { ...category, name: '스위트', gradeList: [] };
        result.push(suite);
      }
      suite.gradeList.push(...(category.gradeList || []));
      return;
    }
    const remaining = [];
    (category.gradeList || []).forEach(room => {
      (isConcierge(room) ? moved : remaining).push(room);
    });
    if (remaining.length) result.push({ ...category, gradeList: remaining });
  });

  if (moved.length) {
    if (!suite) {
      suite = { name: '스위트', codtype: 'suite', gradeList: [] };
      result.push(suite);
    }
    suite.gradeList.push(...moved);
  }

  return result.map(category => {
    // Appending concierge cabins breaks the upstream order. Sort copies by
    // bookable minimum (not the old grade price or an unavailable promotion).
    // Keep equal-price cabins stable and retain the original room/rate objects.
    const pricedRooms = category.gradeList.map((room, index) => ({
      room, index, price: availableMinPrice([room]) ?? Infinity,
    })).sort((a, b) => (a.price - b.price) || (a.index - b.index));
    const minPrice = pricedRooms[0]?.price;
    return {
      ...category,
      gradeList: pricedRooms.map(item => item.room),
      minPrice: Number.isFinite(minPrice) ? minPrice : null,
    };
  }).filter(category => category.gradeList.length && category.minPrice !== null);
}
